import fetchClient from './fetchClient';

import { errorHandler } from './utils';


const deliveryConfig = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('delivery-token'),
        },
    };
};

const config = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    };
};

export const getPaginatedCustomers = async (
    page: number,
    limit: number,
    search: string,
    opts?: { assignedTo?: string; tags?: string | string[]; deliveryTourId?: string },
  ) => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('limit', String(limit));
    if (search) qs.set('search', search);
    if (opts?.assignedTo) qs.set('assignedTo', opts.assignedTo);
  
    if (Array.isArray(opts?.tags)) {
      qs.set('tags', opts.tags.join(','));
    } else if (opts?.tags) {
      qs.set('tags', opts.tags);
    }
  
    if (opts?.deliveryTourId) qs.set('deliveryTourId', opts.deliveryTourId);
  
    const res: any = await fetchClient
      .get(`/customers?${qs.toString()}`, config())
      .catch(errorHandler);
    if (!res) return null;
    return res.data;
  };
  

export const getCustomerTags = async (): Promise<string[] | null> => {
    const res: any = await fetchClient
      .get('/customers/tags', config())
      .catch(errorHandler);
    if (!res) return null;
    // attend un tableau de strings
    return (res.data?.data || res.data || []) as string[];
  };

export const checkCustomerCode = async (code: string, excludeId?: string): Promise<boolean> => {
    if (!code) return true;
    const params = excludeId ? `code=${encodeURIComponent(code)}&excludeId=${excludeId}` : `code=${encodeURIComponent(code)}`;
    const res: any = await fetchClient
        .get(`/customers/check-code?${params}`, config())
        .catch((_err: any) => null);
    if (!res) return false;
    return res.data?.available ?? true;
};

export const getPaginatedCustomersByReturnables = async (
    page: number,
    limit: number,
    search: string,
) => {
    const res: any = await fetchClient
        .get(
            `/customers/returnables?limit=${limit}&page=${page}&search=${search}`,
            config(),
        )
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const shareMagicLink = async (email: string, additionalMails: string[] = []) => {
    const res = await fetchClient
        .post(`/auto-orders/share`, { email, additionalMails }, config())
        .catch(errorHandler);
    return res.data;
};

export const getProspects = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers/prospects?search=${search}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const addCustomerActivity = async (
    id: string,
    content: string,
    type: string,
) => {
    const res = await fetchClient
        .post(`/customers/${id}/activities`, { content, type }, config())
        .catch(errorHandler);
    return res.data;
};

export const addCustomerTasks = async (
    id: string,
    content: string,
    dueDate: string,
) => {
    const res = await fetchClient
        .post(`/customers/${id}/tasks`, { content, dueDate }, config())
        .catch(errorHandler);
    return res.data;
};

export const getCustomers = async () => {
    const res: any = await fetchClient
        .get('/customers?limit=5000', config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const searchCustomers = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers?limit=50&search=${search}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const searchCustomersAndProspects = async (search: string) => {
    const res: any = await fetchClient
        .get(`/customers/all?limit=50&search=${search}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getCustomersWithNotInvoicedOrder = async () => {
    const res: any = await fetchClient
        .get('/customers/invoices', config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getCustomer = async (id: string) => {
    const res = await fetchClient.get(`/customers/${id}`, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerPage = async (id: string, filter: any = null) => {
    let url = `/customers/page/${id}`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerAnalytics = async (id: string, filter: any = null) => {
    let url = `/customers/${id}/analytics`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerOrderedProducts = async (id: string, filter: any = null) => {
    let url = `/customers/${id}/ordered-products`;
    if (filter) {
        const { startDate, endDate } = filter;
        url += `?start=${startDate}&end=${endDate}`;
    }

    const res = await fetchClient.get(url, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const getCustomerLastOrder = async (id: string) => {
    const res = await fetchClient
        .get(`/customers/${id}/lastOrder`, config())
        .catch((err: any) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const updateCustomerDelivery = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}`, customer, deliveryConfig())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const updateCustomer = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}`, customer, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const updatePageActivated = async (customer: any) => {
    const id = customer._id;
    delete customer._id;
    const res: any = await fetchClient
        .put(`/customers/${id}/isOrderingPageActivated`, customer, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const archiveCustomer = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/customers/${id}/archive`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const deleteCustomer = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/customers/${id}`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const createCustomer = async (customer: any) => {
    const res = await fetchClient
        .post('/customers', customer, config())
        .catch(errorHandler);
    return res.data;
};

export const importCustomers = async (customers: any) => {
    const res = await fetchClient
        .post('/customers/bulk', { customers }, config())
        .catch(errorHandler);
    return res.data;
};

export const downloadTotalCustomer = async (customerId: string) => {
    const fileConfig: any = {
        headers: {
            Authorization: config().headers.Authorization,
        },
        responseType: 'arraybuffer',
    };
    const res = await fetchClient
        .get(
            `/customers/products-per-year?customerId=${customerId}`,
            fileConfig,
        )
        .catch((err) => {
            errorHandler(err);
        });
    if (!res || !res.data) return null;
    return new Blob([res.data], { type: 'text/csv' });
};

export const downloadAllCustomers = async () => {
  try {
    const res = await fetchClient.get('/customers/export/csv/customers', {
      ...config(),
      responseType: 'blob',
    });

    if (!res || !res.data) return null;

    const cd = (res.headers?.['content-disposition'] || '') as string;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd || '');
    const filename = decodeURIComponent(match?.[1] || match?.[2] || 'clients.csv');

    const url = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return true;
  } catch (err: any) {
    try {
      const blob = err?.response?.data as Blob | undefined;
      const text = blob ? await blob.text() : '';
      if (text) {
        const json = JSON.parse(text);
        errorHandler({ response: { data: { error: json?.error || 'Export impossible' } } });
      } else {
        errorHandler(err);
      }
    } catch {
      errorHandler(err);
    }
    return null;
  }
};